
import { Component, Mixins, Prop } from 'vue-property-decorator'
import axios from 'axios'
import StepNavigation from '@/components/processing_steps/StepNavigation.vue'
import FormButtons from '@/components/processing_steps/FormButtons.vue'
import FormTitle from '@/components/processing_steps/FormTitle.vue'
import ContactPersonForm from '@/components/processing_steps/contact_person/ContactPersonForm.vue'
import ContactPersonPhoto from '@/components/processing_steps/contact_person/ContactPersonPhoto.vue'
import StepContainer from '../commons/StepContainer.vue'
import ProcessingStepMixin from '../ProcessingStepMixin.vue'
import { IPhoto } from '@/types/photos'
import ValidationMixin from '../ValidationMixin.vue'
import FieldValidationMixin from '../FieldValidationMixin.vue'
import { IEditContactPerson, IPSContactPersonBaseForm, IProcessingStep, IPSSelectOption } from '@/types/processingSteps'
import { API_URLS } from '@/utils/helpers'

@Component({
  components: {
    StepNavigation,
    ContactPersonForm,
    ContactPersonPhoto,
    FormTitle,
    FormButtons,
    StepContainer
  }
})
export default class EditContactPerson extends Mixins(ProcessingStepMixin, ValidationMixin, FieldValidationMixin) {
  @Prop() apiUrl: string
  contactPerson: IEditContactPerson | null = null
  existingPhotos: IPhoto[] = []
  availableOrganizations: IPSSelectOption[] = []

  firstStep: IProcessingStep = {
    key: "basic",
    title: "Basisinformationen",
    completed: false,
    required: true,
    active: true,
    buttons: {
      save: false,
      saveAndNext: false
    }
  }

  secondStep: IProcessingStep = {
    key: "photos",
    title: "Photos",
    completed: false,
    required: false,
    active: false,
    buttons: {
      save: false,
      saveAndNext: false
    }
  }

  contactPersonForm: IPSContactPersonBaseForm = {
    term_of_address: "1",
    academic_title: "",
    first_name: "",
    last_name: "",
    email: "",
    vita: "",
    job_title: "",
    street: "",
    city: "",
    postal_code: "",
    phone: "",
    mobile: "",
    fax: "",
    public_contact_info: "",
    related_organization: ""
  }

  contactPersonFormErrors: IPSContactPersonBaseForm = {
    term_of_address: "",
    academic_title: "",
    first_name: "",
    last_name: "",
    email: "",
    vita: "",
    job_title: "",
    street: "",
    city: "",
    postal_code: "",
    phone: "",
    mobile: "",
    fax: "",
    public_contact_info: "",
    related_organization: ""
  }

  isCreate = false

  async mounted (): Promise<void> {
    this.isCreate = !this.apiUrl
    if (this.isCreate) {
      this.firstStep.buttons.save = false
    }
    this.steps = [this.firstStep, this.secondStep]
    await this.fetchContactPerson()
    await this.fetchUsersOrganizations()
    this.setActiveStep(null)
    this.setContactPersonForm()
    this.updateBreadCrumbs()
  }

  updateBreadCrumbs() {
    this.$emit("breadcrumbs-set", [])
  }

  async fetchContactPerson (): Promise<void> {
    if (this.isCreate) return
    await axios.get(this.apiUrl).then((response) => {
      this.contactPerson = response.data
      this.existingPhotos = this.contactPerson.photo
    })
  }

  setContactPersonForm (): void {
    this.availableOrganizations = this.usersOrganizations
    if (this.contactPerson === null) {
      this.contactPersonForm.related_organization = this.availableOrganizations && this.availableOrganizations.length > 0 ? this.availableOrganizations[0].value : ""
      return
    }
    this.contactPersonForm.term_of_address = this.contactPerson.term_of_address
    this.contactPersonForm.academic_title = this.contactPerson.academic_title
    this.contactPersonForm.first_name = this.contactPerson.first_name
    this.contactPersonForm.last_name = this.contactPerson.last_name
    this.contactPersonForm.email = this.contactPerson.email
    this.contactPersonForm.vita = this.contactPerson.vita
    this.contactPersonForm.job_title = this.contactPerson.job_title
    this.contactPersonForm.street = this.contactPerson.street
    this.contactPersonForm.city = this.contactPerson.city
    this.contactPersonForm.postal_code = this.contactPerson.postal_code
    this.contactPersonForm.phone = this.contactPerson.phone
    this.contactPersonForm.mobile = this.contactPerson.mobile
    this.contactPersonForm.fax = this.contactPerson.fax
    this.contactPersonForm.public_contact_info = this.contactPerson.public_contact_info
    this.contactPersonForm.related_organization = this.contactPerson.related_organization.slug
  }

  nextBtnClicked (): void {
    this.gotoNextStepOrFinish(this.steps, this.activeStep, "contact_person")
  }

  previousBtnClicked (currentStep: IProcessingStep): void {
    this.gotoPreviousStep(this.steps, currentStep)
  }

  handlePhotosChanged (): void {
    this.fetchContactPerson()
  }

  getUpdateUrl (contactPersonSlug): string {
    if (!contactPersonSlug) {
      return ""
    }
    if (this.isSiteAdmin) {
      return "/site-admin/contactpersons/" + contactPersonSlug + "/update/"
    } else {
      return "/account/contactpersons/" + contactPersonSlug + "/update/"
    }
  }

  async onFileInputClicked (): Promise<void> {
    await this.saveBaseInformation()
  }

  get contactPersonDataIsComplete (): boolean {
    return this.contactPersonForm.first_name.trim().length > 0 &&
      this.contactPersonForm.last_name.trim().length > 0 &&
      this.isValidEmail(this.contactPersonForm.email)
  }

  async saveBaseInformation (): Promise<boolean> {
    let saved = false

    const requestData = {
      ...this.contactPersonForm,
      related_organization_slug: this.contactPersonForm.related_organization
    }

    try {
      let response
      if (this.isCreate) {
        response = await axios.post(API_URLS.CONTACT_PERSON.LIST, requestData)
      } else {
        response = await axios.put(API_URLS.CONTACT_PERSON.RETRIEVE(this.contactPerson.slug), requestData)
      }

      if (response.status === 200) {
        saved = true
      } else {
        this.contactPersonFormErrors = response.data
      }
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        this.showRequiredFieldsError()
        const errors = error.response.data
        Object.keys(errors).forEach(key => {
          if (Array.isArray(errors[key])) {
            this.contactPersonFormErrors[key] = errors[key].join(' ')
          } else {
            this.contactPersonFormErrors[key] = errors[key]
          }
        })
      } else {
        this.showContactAdminError()
      }
    }

    if (saved) {
      this.$emit("created-or-updated", saved)
    }

    return saved
  }

  finish (): void {
    this.$emit("finish")
  }
}
